// The namespace, not named imports: a kit from before the receive pages (29 Oct) has none of these, and a named import
// would fail the editor's load instead of this one action.
import * as kitUi from '@flobi/asset-library/ui';
import { assetsMap, colorToHex, fontsMap, invert, multiply, type Asset, type DocumentFont, type Matrix, type NodeRecord } from '@flobi/artwork-core';
import apiClient from '../services/apiClient';

/*
 * Send to Flow on an artboard (cross-app pages, 29 Oct 2026): the artboard as a LAYERED FILE inside a "layers" parcel,
 * which Flow's /receive page turns into a Layer node with every layer placed. The file is the one Flow already sends the
 * other way (flobi-flow lib/layers/artworkFile.js `layersNodeFile`) and Artwork reads (layers/layerFile.ts): bottom
 * first, boxes in the artboard's pixels from its top left, pictures by media asset, text as text.
 *
 * Flow's Layer node takes pictures and text. Everything else on the page — shapes, paths, booleans, blends, repeats,
 * masks, a picture that is not a media asset, a clip — is left out and counted, so the send can say so. Groups are
 * opened out, their pictures and text kept in draw order. A turned layer is placed by its unturned box.
 */

export const LAYER_FILE_KIND = 'flobi.layers';

interface Box { x: number; y: number; w: number; h: number }
type TextStyleOut = {
  fontFamily: string; fontWeight: number; fontSizePx: number; color?: string; letterSpacingPx: number; lineHeight: number;
  align: 'left' | 'center' | 'right'; alignY: 'top' | 'middle' | 'bottom'; uppercase?: true; italic?: true; underline?: true;
};
export type FlowLayer =
  | { id: string; kind: 'fill'; name?: string; fill: string; bbox: Box; z: number; visible?: false }
  | { id: string; kind: 'image'; name?: string; bbox: Box; z: number; result: number; fit: { mode: 'fill' }; visible?: false }
  | { id: string; kind: 'text'; name?: string; bbox: Box; z: number; wrap: true; text: string; style: TextStyleOut; visible?: false };
export interface FlowPicture { mediaAssetId: string; url: string; mime: string; name: string; width?: number; height?: number }
export interface FlowLayersFile {
  kind: typeof LAYER_FILE_KIND; version: 1; name: string; model: 'layers'; editableText: boolean;
  canvas: { w: number; h: number }; size: { width: number; height: number }; fonts: string[];
  layers: FlowLayer[]; pictures: FlowPicture[]; preview: { url: string; mediaAssetId?: string } | null;
}

/** What the builder reads of an open file: its page's scene, its assets and its fonts. */
export interface FlowSource {
  scene: { get(id: string): NodeRecord | undefined; childrenOf(id: string): readonly string[]; worldTransform(id: string): Matrix };
  asset(id: string): Asset | undefined;
  fonts: readonly DocumentFont[];
}

/** The open file's parts as the builder reads them. */
export function flowSourceOf(open: { scene: FlowSource['scene']; root: import('yjs').Doc }): FlowSource {
  const assets = assetsMap(open.root);
  return { scene: open.scene, asset: (id) => assets.get(id) as Asset | undefined, fonts: [...fontsMap(open.root).values()] as DocumentFont[] };
}

type Loose = Record<string, any>;
const HTTPS = /^https:\/\//i;
const num = (v: unknown, fallback: number) => (Number.isFinite(Number(v)) ? Number(v) : fallback);
const hex6 = (c: { r: number; g: number; b: number }) => colorToHex({ ...c, a: 1 });
/** Types that draw nothing on the page, so leaving them behind loses nothing. */
const DRAWS_NOTHING = new Set(['effector', 'slice']);
/** Auto-width text is set on one line at its measured width; another browser's face may measure a hair wider. */
const LINE_SLACK = 1.04;

/** The colour a paint list shows as one hex: its top visible solid, else a gradient's first stop. */
function paintColour(paints: readonly Loose[] | undefined): string | undefined {
  const shown = (paints ?? []).filter((p) => p && p.visible !== false);
  const solid = [...shown].reverse().find((p) => p.type === 'solid' && p.color);
  if (solid) return hex6(solid.color);
  const stop = shown.find((p) => Array.isArray(p.stops) && p.stops[0]?.color)?.stops[0].color;
  return stop ? hex6(stop) : undefined;
}

/**
 * The box a layer is drawn in on the artboard, unturned: its own box scaled by its transform (relative to the
 * artboard), about where its middle lands.
 */
function drawnBox(m: Matrix, w: number, h: number): { box: Box; sx: number; sy: number } {
  const sx = Math.hypot(m[0], m[1]) || 1;
  const sy = Math.hypot(m[2], m[3]) || 1;
  const cx = m[0] * (w / 2) + m[2] * (h / 2) + m[4];
  const cy = m[1] * (w / 2) + m[3] * (h / 2) + m[5];
  const bw = Math.max(1, w * sx), bh = Math.max(1, h * sy);
  return { box: { x: cx - bw / 2, y: cy - bh / 2, w: bw, h: bh }, sx, sy };
}

/** A text layer's style as the file keeps it, at the size it is drawn (`sy`, `sx` the layer's scale on the artboard). */
export function textStyleOut(node: Loose, sx: number, sy: number): TextStyleOut {
  const style = { ...(node.style ?? {}), ...((node.runs?.[0]?.style as Loose | undefined) ?? {}) } as Loose;
  const size = Math.max(1, num(style.fontSize, 16) * sy);
  const lh = style.lineHeight;
  const lineHeight = lh && typeof lh === 'object' ? (lh.unit === 'percent' ? num(lh.value, 120) / 100 : (num(lh.value, size) * sy) / size) : 1.2;
  const ls = style.letterSpacing;
  const letterSpacingPx = ls && typeof ls === 'object' ? (ls.unit === 'px' ? num(ls.value, 0) * sx : (num(ls.value, 0) / 100) * size) : 0;
  const colour = paintColour(style.fills);
  return {
    fontFamily: String(style.fontFamily || 'Inter'),
    fontWeight: Math.round(num(style.fontWeight, 400)),
    fontSizePx: size,
    ...(colour ? { color: colour } : {}),
    letterSpacingPx,
    lineHeight: lineHeight > 0 ? lineHeight : 1.2,
    align: node.align === 'center' ? 'center' : node.align === 'end' ? 'right' : 'left',
    alignY: node.verticalAlign === 'middle' ? 'middle' : node.verticalAlign === 'bottom' ? 'bottom' : 'top',
    ...(style.textCase === 'upper' ? { uppercase: true as const } : {}),
    ...(style.fontStyle === 'italic' ? { italic: true as const } : {}),
    ...(style.decoration === 'underline' ? { underline: true as const } : {}),
  };
}

/** The asset an image layer, or a rectangle filled with one picture and nothing else, shows; else null. */
function pictureAssetId(node: Loose): string | null {
  if (node.type === 'image') return node.clip ? null : typeof node.assetId === 'string' ? node.assetId : null;
  if (node.type !== 'rect') return null;
  const shown = (node.fills ?? []).filter((p: Loose) => p && p.visible !== false);
  return shown.length === 1 && shown[0].type === 'image' && typeof shown[0].assetId === 'string' ? shown[0].assetId : null;
}

/** A Google family's stylesheet with the faces asked for, as Flow names one (flobi-flow lib/appNodes/fonts googleSheetOf). */
function googleSheet(family: string, faces: readonly { weight?: number; italic?: boolean }[]): string {
  const fam = encodeURIComponent(family).replace(/%20/g, '+');
  const tuples = [...new Set(faces.filter((f) => num(f.weight, 0) > 0).map((f) => `${f.italic ? 1 : 0},${Math.round(num(f.weight, 400))}`))].sort((a, b) => {
    const [ia, wa] = a.split(',').map(Number);
    const [ib, wb] = b.split(',').map(Number);
    return ia! - ib! || wa! - wb!;
  });
  return tuples.length ? `https://fonts.googleapis.com/css2?family=${fam}:ital,wght@${tuples.join(';')}&display=swap` : `https://fonts.googleapis.com/css2?family=${fam}&display=swap`;
}

/**
 * The fonts a file's texts need, as Flow's Layer node lists them: a Google family as its stylesheet, a Flobi Fonts face
 * as `flobi-font:<id>#<family>` (flobi-flow lib/appNodes/fonts faceEntry). Families the file does not carry are left
 * to Flow's own lookup.
 */
export function fontEntries(families: Iterable<string>, fonts: readonly DocumentFont[]): string[] {
  const out: string[] = [];
  for (const family of new Set(families)) {
    const font = fonts.find((f) => f.family.toLowerCase() === family.toLowerCase());
    if (!font) continue;
    const ids = [...new Set(font.faces.map((f) => f.fontId).filter((id): id is string => !!id))];
    if (ids.length) out.push(...ids.map((id) => `flobi-font:${id}#${encodeURIComponent(font.family)}`));
    else if (font.source === 'google' || font.source === 'builtin') out.push(googleSheet(font.family, font.faces));
  }
  return [...new Set(out)];
}

/**
 * Artboard `artboardId` as a layered file for Flow, with how many layers were left out; null when it is not an
 * artboard or Flow could take nothing on it (no picture and no text).
 */
export function artboardLayersFile(source: FlowSource, artboardId: string): { file: FlowLayersFile; leftOut: number } | null {
  const { scene } = source;
  const board = scene.get(artboardId) as Loose | undefined;
  if (!board || board.type !== 'artboard') return null;
  const w = Math.round(num(board.width, 0)), h = Math.round(num(board.height, 0));
  if (!(w > 0) || !(h > 0)) return null;
  const toBoard = invert(scene.worldTransform(artboardId));
  if (!toBoard) return null;

  const layers: FlowLayer[] = [];
  const pictures: FlowPicture[] = [];
  const pictureAt = new Map<string, number>();
  const families: string[] = [];
  let leftOut = 0;

  /** The picture `assetId` stands for, as an index into `pictures`; undefined when it is not a media asset. */
  const picture = (assetId: string, name: string): number | undefined => {
    const asset = source.asset(assetId);
    if (!asset || asset.kind !== 'image' || !asset.mediaAssetId || !asset.src || !HTTPS.test(asset.src)) return undefined;
    if (!pictureAt.has(asset.mediaAssetId)) {
      pictureAt.set(asset.mediaAssetId, pictures.push({
        mediaAssetId: asset.mediaAssetId, url: asset.src, mime: asset.mime || 'image/png', name: asset.name || name || `Picture ${pictures.length + 1}`,
        ...(num(asset.width, 0) > 0 && num(asset.height, 0) > 0 ? { width: asset.width!, height: asset.height! } : {}),
      }) - 1);
    }
    return pictureAt.get(asset.mediaAssetId);
  };

  // The artboard's own paint, bottom first: a solid is the background colour, a picture a picture filling the page.
  (board.fills ?? []).filter((p: Loose) => p && p.visible !== false).forEach((paint: Loose, i: number) => {
    const bbox = { x: 0, y: 0, w, h };
    const id = `${artboardId}-fill-${i}`;
    if (paint.type === 'solid' && paint.color) layers.push({ id, kind: 'fill', name: 'Background', fill: hex6(paint.color), bbox, z: layers.length });
    else {
      const result = paint.type === 'image' && typeof paint.assetId === 'string' ? picture(paint.assetId, 'Background') : undefined;
      if (result === undefined) leftOut += 1;
      else layers.push({ id, kind: 'image', name: 'Background', bbox, z: layers.length, result, fit: { mode: 'fill' } });
    }
  });

  const walk = (parentId: string, hidden: boolean) => {
    for (const id of scene.childrenOf(parentId)) {
      const node = scene.get(id) as Loose | undefined;
      if (!node || DRAWS_NOTHING.has(node.type)) continue;
      const off = hidden || node.visible === false;
      if (node.type === 'group') {
        walk(id, off);
        continue;
      }
      const m = multiply(toBoard, scene.worldTransform(id));
      const name = String(node.name || '').slice(0, 200) || undefined;
      const shared = { id, ...(name ? { name } : {}), ...(off ? { visible: false as const } : {}) };
      const assetId = node.isMask ? null : pictureAssetId(node);
      if (assetId) {
        const result = picture(assetId, name ?? '');
        if (result === undefined) leftOut += 1;
        else layers.push({ ...shared, kind: 'image', bbox: drawnBox(m, num(node.width, 0), num(node.height, 0)).box, z: layers.length, result, fit: { mode: 'fill' } });
        continue;
      }
      const text = node.type === 'text' && !node.isMask ? (node.runs ?? []).map((run: Loose) => String(run?.text ?? '')).join('') : '';
      if (!text.trim()) {
        // A shape, a path, a boolean, a blend, a repeat, a mask, an empty text: Flow's Layer node has none of these.
        leftOut += 1;
        continue;
      }
      const { box, sx, sy } = drawnBox(m, num(node.width, 0), num(node.height, 0));
      const style = textStyleOut(node, sx, sy);
      let bbox = box;
      if (node.sizing === 'auto-width' && !text.includes('\n')) {
        const wide = box.w * LINE_SLACK + 2;
        bbox = { ...box, w: wide, x: style.align === 'left' ? box.x : style.align === 'right' ? box.x + box.w - wide : box.x - (wide - box.w) / 2 };
      }
      families.push(style.fontFamily);
      layers.push({ ...shared, kind: 'text', bbox, z: layers.length, wrap: true, text, style });
    }
  };
  walk(artboardId, false);
  if (!layers.some((layer) => layer.kind !== 'fill')) return null;

  const name = String(board.name || '').trim().slice(0, 200) || 'Artboard';
  return {
    leftOut,
    file: {
      kind: LAYER_FILE_KIND, version: 1, name, model: 'layers', editableText: layers.some((layer) => layer.kind === 'text'),
      canvas: { w, h }, size: { width: w, height: h }, fonts: fontEntries(families, source.fonts), layers, pictures, preview: null,
    },
  };
}

/** "3 layers left out: Flow takes pictures and text." — empty when nothing was. */
export function leftOutWords(leftOut: number): string {
  return leftOut > 0 ? `${leftOut} ${leftOut === 1 ? 'layer' : 'layers'} left out: Flow takes pictures and text.` : '';
}

type Tab = { go: (url: string) => void; close: () => void };
type Poster = (getToken: unknown, path: string, data: unknown) => Promise<unknown>;
interface Kit {
  openTab?: () => Tab;
  parcelBody?: (body: Record<string, unknown>) => Record<string, unknown>;
  writeParcel?: (body: Record<string, unknown>, options: { poster: Poster }) => Promise<string>;
  receiveUrl?: (app: string, id: string) => string;
}

/** The kit's calls go through Artwork's own client: Artwork never points the kit at the gateway itself. */
const poster: Poster = (_getToken, path, data) => apiClient.post(path, data).then((response) => response?.data);

/**
 * Sends artboard `artboardId` to Flow as a layers parcel and opens Flow's /receive page on it; answers how many layers
 * were left out. Throws a sentence for the person.
 */
export async function sendArtboardToFlow(source: FlowSource, artboardId: string): Promise<{ leftOut: number }> {
  const { openTab, parcelBody, writeParcel, receiveUrl } = kitUi as unknown as Kit;
  if (!openTab || !parcelBody || !writeParcel || !receiveUrl) throw new Error('Sending to Flow needs the latest Flobi kit. Update it, then try again.');
  const built = artboardLayersFile(source, artboardId);
  if (!built) throw new Error('There is nothing on this artboard Flow can take: Flow takes pictures and text.');
  const { file, leftOut } = built;
  // Opened at the press: a tab opened after the call below is a popup the browser blocks.
  const tab = openTab();
  try {
    const body = parcelBody({
      type: 'layers', name: file.name, from: { app: 'artwork', id: artboardId, name: file.name },
      size: file.size, items: [], payload: { file, leftOut },
    });
    const id = await writeParcel(body, { poster });
    const url = receiveUrl('flow', id);
    if (!url) throw new Error('Flow could not be opened. Try again.');
    tab.go(url);
    return { leftOut };
  } catch (error) {
    tab.close();
    throw error;
  }
}
